import { useEffect, useState } from "react";
import api from "../api/client";
import ProjectNav from "../components/ProjectNav";

const fmt = (v: number | null | undefined, prefix = "$") => {
  if (v === null || v === undefined) return "—";
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (abs >= 1000000) return `${sign}${prefix}${(abs / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `${sign}${prefix}${(abs / 1000).toFixed(0)}K`;
  return `${sign}${prefix}${abs.toFixed(2)}`;
};

const cpiColor = (v: number) => v < 0.9 ? "text-red-600" : v < 0.95 ? "text-amber-500" : "text-green-600";
const varColor = (v: number) => v < 0 ? "text-red-600" : "text-green-600";

const Delta = ({ a, b, prefix = "$", invert = false }: any) => {
  if (a === null || b === null || a === undefined || b === undefined) return <span className="text-gray-400 text-xs">—</span>;
  const diff = b - a;
  const pct = a !== 0 ? ((diff / Math.abs(a)) * 100).toFixed(1) : "—";
  const good = invert ? diff < 0 : diff > 0;
  const color = diff === 0 ? "text-gray-400" : good ? "text-green-600" : "text-red-500";
  const arrow = diff > 0 ? "↑" : diff < 0 ? "↓" : "→";
  return (
    <div className={`text-xs font-medium ${color}`}>
      {arrow} {fmt(Math.abs(diff), prefix)} ({pct}%)
    </div>
  );
};

export default function Comparison() {
  const [reports, setReports] = useState<any[]>([]);
  const [reportA, setReportA] = useState("");
  const [reportB, setReportB] = useState("");
  useEffect(() => {
    api.get("/api/reports?project_id=" + (localStorage.getItem("active_project_id") || "")).then(r => {
      const sorted = r.data.filter((r: any) => r.output);
      setReports(sorted);
      if (sorted.length >= 2) {
        setReportA(sorted[1].id);
        setReportB(sorted[0].id);
      }
    });
  }, []);

  const getReport = (id: string) => reports.find(r => r.id === id);
  const rA = getReport(reportA);
  const rB = getReport(reportB);
  const mA = rA?.output?.executive_summary?.metrics;
  const mB = rB?.output?.executive_summary?.metrics;
  const vA = rA?.output?.variances?.variance_list || [];
  const vB = rB?.output?.variances?.variance_list || [];

  const MetricRow = ({ label, keyA, keyB, prefix = "$", invert = false, isIndex = false }: any) => {
    const a = mA?.[keyA];
    const b = mB?.[keyB];
    return (
      <tr className="border-b border-gray-50 hover:bg-gray-50">
        <td className="px-4 py-3 text-sm text-gray-600">{label}</td>
        <td className={`px-4 py-3 text-sm font-bold text-right ${isIndex ? cpiColor(a) : varColor(a)}`}>
          {isIndex ? a?.toFixed(3) : fmt(a, prefix)}
        </td>
        <td className="px-4 py-3 text-center">
          <Delta a={a} b={b} prefix={isIndex ? "" : prefix} invert={invert} />
        </td>
        <td className={`px-4 py-3 text-sm font-bold text-right ${isIndex ? cpiColor(b) : varColor(b)}`}>
          {isIndex ? b?.toFixed(3) : fmt(b, prefix)}
        </td>
      </tr>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ProjectNav projectId={localStorage.getItem("active_project_id") || ""} active="history" />

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">COMPARISON VIEW</h1>
            <p className="text-xs text-gray-400 mt-0.5">SIDE BY SIDE REPORT COMPARISON WITH DELTA INDICATORS</p>
          </div>
        </div>

        {/* Report selectors */}
        <div className="grid grid-cols-3 gap-4 mb-6 items-center">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-400 mb-2">BASELINE (Earlier)</p>
            <select value={reportA} onChange={e => setReportA(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              {reports.map(r => <option key={r.id} value={r.id}>{r.version_label} — {r.quarter}</option>)}
            </select>
            {rA && (
              <div className="mt-2 text-xs text-gray-500">
                {new Date(rA.created_at).toLocaleDateString()} · {rA.status}
              </div>
            )}
          </div>

          <div className="flex flex-col items-center gap-2">
            <div className="w-10 h-10 bg-blue-50 border border-blue-200 rounded-full flex items-center justify-center">
              <span className="text-blue-600 text-lg">⇄</span>
            </div>
            <p className="text-xs text-gray-400">vs</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-400 mb-2">COMPARE TO (Later)</p>
            <select value={reportB} onChange={e => setReportB(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              {reports.map(r => <option key={r.id} value={r.id}>{r.version_label} — {r.quarter}</option>)}
            </select>
            {rB && (
              <div className="mt-2 text-xs text-gray-500">
                {new Date(rB.created_at).toLocaleDateString()} · {rB.status}
              </div>
            )}
          </div>
        </div>

        {mA && mB ? (
          <div className="space-y-4">
            {/* Key metrics comparison */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <p className="text-sm font-semibold text-gray-900">Key Metrics Comparison</p>
              </div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Metric</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">{rA?.version_label}</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Change</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">{rB?.version_label}</th>
                  </tr>
                </thead>
                <tbody>
                  <MetricRow label="CPI (Cost Performance)" keyA="cpi" keyB="cpi" prefix="" isIndex invert />
                  <MetricRow label="SPI (Schedule Performance)" keyA="spi" keyB="spi" prefix="" isIndex invert />
                  <MetricRow label="EAC (Estimate at Completion)" keyA="eac" keyB="eac" invert />
                  <MetricRow label="BAC (Budget at Completion)" keyA="bac" keyB="bac" />
                  <MetricRow label="VAC (Variance at Completion)" keyA="vac" keyB="vac" />
                  <MetricRow label="% Complete" keyA="pct_complete" keyB="pct_complete" prefix="" />
                </tbody>
              </table>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: "EAC Change", val: mB.eac - mA.eac, sub: "Higher = more cost overrun", invert: true },
                { label: "CPI Change", val: mB.cpi - mA.cpi, prefix: "", sub: "Higher = better cost efficiency", invert: false },
                { label: "VAC Change", val: mB.vac - mA.vac, sub: "Higher = less overrun", invert: false },
              ].map(c => (
                <div key={c.label} className="bg-white rounded-xl border border-gray-200 p-4">
                  <p className="text-xs text-gray-400 mb-1">{c.label}</p>
                  <p className={`text-xl font-bold ${(c.invert ? c.val < 0 : c.val > 0) ? "text-green-600" : c.val < 0 ? "text-red-600" : "text-gray-500"}`}>
                    {c.val > 0 ? "+" : ""}{c.prefix !== "" ? fmt(c.val) : c.val.toFixed(3)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">{c.sub}</p>
                </div>
              ))}
            </div>

            {/* Variance comparison */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <p className="text-sm font-semibold text-gray-900 mb-3">{rA?.version_label} — Top Variances</p>
                <div className="space-y-2">
                  {vA.slice(0, 6).map((v: any, i: number) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-gray-50 last:border-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono bg-blue-50 text-blue-700 px-1.5 rounded">{v.obs}</span>
                        <span className="text-gray-600 truncate max-w-24">{v.description}</span>
                      </div>
                      <span className="text-red-600 font-medium">{fmt(v.dollar_amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <p className="text-sm font-semibold text-gray-900 mb-3">{rB?.version_label} — Top Variances</p>
                <div className="space-y-2">
                  {vB.slice(0, 6).map((v: any, i: number) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-gray-50 last:border-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono bg-blue-50 text-blue-700 px-1.5 rounded">{v.obs}</span>
                        <span className="text-gray-600 truncate max-w-24">{v.description}</span>
                      </div>
                      <span className="text-red-600 font-medium">{fmt(v.dollar_amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <p className="text-gray-400 text-sm">Select two reports above to see comparison</p>
          </div>
        )}
      </div>
    </div>
  );
}
